/**
 * ApplyResultNextCard — kompakte „Als Nächstes"-Karte im Ergebnis-Dialog
 * (P4/B6a, T13): erste Karte aus `useNbaCards`, Reuse der
 * `productsIssues.nba.*`-Texte/CTAs (T10/`NbaCardsRow.tsx`) statt neuer Texte.
 * `rule_gap` öffnet hier bewusst keinen verschachtelten Dialog (Scope-Limit).
 */
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/v2/ui'
import type { NbaCard } from '@/lib/api/issues-insights'
import { EID } from '@/lib/element-ids.registry'

function NextCardShell({ tag, title, children }: { tag: string; title: string; children?: React.ReactNode }) {
  return (
    <div className="space-y-1.5 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
      <span className="text-xs font-semibold uppercase text-neutral-400">{tag}</span>
      <p className="text-sm text-neutral-900 dark:text-neutral-100">{title}</p>
      {children}
    </div>
  )
}

export function ApplyResultNextCard({ card }: { card: NbaCard }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const cta = (label: string, onClick: () => void) => (
    <Button id={EID['text-bulk-apply-result-next-cta-btn']} data-testid="apply-result-next-cta" size="sm" onClick={onClick}>
      {label}
    </Button>
  )

  if (card.type === 'top_seller_fix')
    return (
      <NextCardShell tag={t('productsIssues.nba.tagTopSeller')} title={t('productsIssues.nba.topSellerTitle', { count: card.product_count, channel: card.channel_name, revenue: card.revenue_30d_sum.toLocaleString('de-DE') })}>
        {cta(t('productsIssues.nba.startRun'), () => navigate(`/v2/tools/text/bulk?products=${card.product_ids.join(',')}&channel=${card.channel_id}`))}
      </NextCardShell>
    )

  if (card.type === 'rule_gap') return <NextCardShell tag={t('productsIssues.nba.tagRuleGap')} title={t('productsIssues.nba.ruleGapTitle', { channel: card.channel_name })} />

  return (
    <NextCardShell tag={t('productsIssues.nba.tagPending')} title={t('productsIssues.nba.pendingTitle', { count: card.count })}>
      {cta(t('productsIssues.nba.review'), () => navigate(card.run_id ? `/v2/products/issues/runs/${card.run_id}` : '/v2/products/issues/runs'))}
    </NextCardShell>
  )
}
